import React, { useState, useMemo } from "react";

import { useMutation } from "@apollo/client";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";

import Button from "components/common/Button";
import Modal from "components/common/Modal";

import useModal from "hooks/useModal";

import getProfileName from "utils/getProfileName";

import UPDATE_CLIENT from "@gql/mutations/clients/updateClient";

import { SELECT_COUNSEL_FOR_CHILDREN_MODAL_NAME } from "./SelectCounselForChildrenModal";

export const SELECT_CHILDREN_FOR_PARTIES_MODAL_NAME = "SELECT_CHILDREN_FOR_PARTIES";

const SelectChildrenForPartiesModal = () => {
  const { options, close } = useModal(SELECT_CHILDREN_FOR_PARTIES_MODAL_NAME);
  const { open: openCounselModal } = useModal(SELECT_COUNSEL_FOR_CHILDREN_MODAL_NAME);
  const { client } = options || {};

  const [selectedChildren, setSelectedChildren] = useState(new Set());
  const [updateClient, { loading }] = useMutation(UPDATE_CLIENT);

  // Get children that are not already parties
  const availableChildren = useMemo(() => {
    if (!client?.children) return [];

    return client.children.filter((child) => !child.isParty);
  }, [client?.children]);

  const handleChildToggle = (childId) => {
    const newSelected = new Set(selectedChildren);
    if (newSelected.has(childId)) {
      newSelected.delete(childId);
    } else {
      newSelected.add(childId);
    }
    setSelectedChildren(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedChildren.size === availableChildren.length) {
      setSelectedChildren(new Set());
    } else {
      setSelectedChildren(new Set(availableChildren.map((child) => child.id)));
    }
  };

  const handleSubmit = async () => {
    if (selectedChildren.size === 0) {
      toast.error("Please select at least one child to add as a party");
      return;
    }

    try {
      const updateOperations = Array.from(selectedChildren).map((childId) => ({
        where: { id: childId },
        data: {
          isParty: { set: true },
        },
      }));

      await updateClient({
        variables: {
          where: { id: client.id },
          data: {
            children: {
              update: updateOperations,
            },
          },
        },
      });

      toast.success(
        `Successfully added ${selectedChildren.size} ${
          selectedChildren.size === 1 ? "child" : "children"
        } as ${selectedChildren.size === 1 ? "a party" : "parties"}`,
      );

      // Open the counsel selection modal for the newly added children
      const addedChildrenIds = Array.from(selectedChildren);
      openCounselModal({
        client,
        addedChildrenIds,
      });

      // Modal will auto-close on successful completion
    } catch (error) {
      console.error("Error updating children:", error);
      toast.error("Failed to update children. Please try again.");
      throw error; // Re-throw to prevent modal from closing on error
    }
  };

  if (!client || availableChildren.length === 0) {
    return (
      <Modal
        showCloseButton
        name={SELECT_CHILDREN_FOR_PARTIES_MODAL_NAME}
        title="Add Children as Parties"
        size="md"
        noFooter
      >
        <div className="text-center py-4">
          <p className="text-muted">
            {!client
              ? "No client data available."
              : "All children are already added as parties or there are no children in this case."}
          </p>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      showCloseButton
      name={SELECT_CHILDREN_FOR_PARTIES_MODAL_NAME}
      title="Add Children as Parties"
      size="md"
      submitButtonTitle="Add Selected Children"
      onSubmitClick={handleSubmit}
      submitBtnDisabled={selectedChildren.size === 0 || loading}
      cancelBtnTitle="Cancel"
    >
      <div className="select-children-modal-content">
        <p className="text-muted mb-3">
          Select which children should be added as parties to this case. Children added as parties
          will appear in the Minor Children section and can be assigned representation.
        </p>

        <div className="mb-3">
          <Form.Check
            type="checkbox"
            id="select-all"
            label={`Select all (${availableChildren.length} children)`}
            checked={selectedChildren.size === availableChildren.length}
            onChange={handleSelectAll}
            className="fw-bold"
          />
        </div>

        <div className="children-list">
          {availableChildren.map((child) => {
            const childName =
              getProfileName(child, false) || `Child ${child.firstName || "Unnamed"}`;
            const isSelected = selectedChildren.has(child.id);

            return (
              <div key={child.id} className="child-item mb-2 p-3 border rounded">
                <Form.Check
                  type="checkbox"
                  id={`child-${child.id}`}
                  label={
                    <div>
                      <div className="fw-medium">{childName}</div>
                      {child.birthDate && (
                        <small className="text-muted">
                          Born: {new Date(child.birthDate).toLocaleDateString()}
                        </small>
                      )}
                    </div>
                  }
                  checked={isSelected}
                  onChange={() => handleChildToggle(child.id)}
                />
              </div>
            );
          })}
        </div>

        {selectedChildren.size > 0 && (
          <div className="mt-3 p-2 bg-light rounded">
            <small className="text-muted">
              {selectedChildren.size} {selectedChildren.size === 1 ? "child" : "children"} selected
              to be added as {selectedChildren.size === 1 ? "a party" : "parties"}
            </small>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SelectChildrenForPartiesModal;
